// IMPORTANT: Changes to enums, labels, or interfaces in this file must be kept in sync
// with the corresponding Python models and logic in the infra repository.
// Any updates here (such as adding/removing vessel types, risk levels, or changing structure)
// may require updates to backend validation, data serialization, or API contracts.
// Please coordinate changes with the infra/backend team to avoid breaking integration.

import bulk from '../assets/icons/my-fleet-icons/bulk.svg';
import chemical_tanker from '../assets/icons/my-fleet-icons/chemical_tanker.svg';
import container from '../assets/icons/my-fleet-icons/container.svg';
import gas_carrier from '../assets/icons/my-fleet-icons/gas_carrier.svg';
import genral_cargo from '../assets/icons/my-fleet-icons/genral_cargo.svg';
import oil_tanker from '../assets/icons/my-fleet-icons/oil_tanker.svg';
import passenger from '../assets/icons/my-fleet-icons/passenger.svg';
import passenger_cargo from '../assets/icons/my-fleet-icons/passenger_cargo.svg';
import reefer from '../assets/icons/my-fleet-icons/reefer.svg';
import roro from '../assets/icons/my-fleet-icons/roro.svg';
import specialist from '../assets/icons/my-fleet-icons/specialist.svg';
import unknown from '../assets/icons/my-fleet-icons/unknown.svg';

export enum RiskLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  UNKNOWN = 'UNKNOWN',
}

const RiskLevelColors: Record<RiskLevel, string> = {
  [RiskLevel.LOW]: '#4BBF7C',
  [RiskLevel.MEDIUM]: '#F5A100',
  [RiskLevel.HIGH]: '#D12721',
  [RiskLevel.UNKNOWN]: '#B2B2B2',
};

export function getRiskLevelColor(riskLevel: RiskLevel): string {
  return RiskLevelColors[riskLevel] || RiskLevelColors[RiskLevel.LOW];
}

export const RiskLevelLabels: Record<RiskLevel, string> = {
  [RiskLevel.LOW]: 'Low',
  [RiskLevel.MEDIUM]: 'Medium',
  [RiskLevel.HIGH]: 'High',
  [RiskLevel.UNKNOWN]: 'Unknown',
};

export function getRiskLevelLabel(level: RiskLevel): string {
  return RiskLevelLabels[level] || level;
}

export enum ProVesselTypes {
  NO_VESSEL_TYPE = 'NO_VESSEL_TYPE', // Added until API response is updated for UNKNOWN vessel type
  BULK = 'BULK',
  CHEMICAL_PRODUCT_TANKER = 'CHEMICAL_PRODUCT_TANKER',
  CONTAINER = 'CONTAINER',
  GAS_CARRIER = 'GAS_CARRIER',
  GENERAL_CARGO = 'GENERAL_CARGO',
  OIL_TANKER = 'OIL_TANKER',
  PASSENGER = 'PASSENGER',
  PASSENGER_CARGO = 'PASSENGER_CARGO',
  REEFER = 'REEFER',
  RORO = 'RORO',
  SPECIALIST = 'SPECIALIST',
  UNKNOWN = 'UNKNOWN',
}

// Ticket #NSP-481 raised to update when color scheme is finalized
export const ProVesselTypeIconColors: Record<ProVesselTypes, string> = {
  [ProVesselTypes.NO_VESSEL_TYPE]: '#ce6eac',
  [ProVesselTypes.BULK]: '#f4a21e',
  [ProVesselTypes.CHEMICAL_PRODUCT_TANKER]: '#d12927',
  [ProVesselTypes.CONTAINER]: '#293578',
  [ProVesselTypes.GAS_CARRIER]: '#b3b3b3',
  [ProVesselTypes.GENERAL_CARGO]: '#52bc7c',
  [ProVesselTypes.OIL_TANKER]: '#07172e',
  [ProVesselTypes.PASSENGER]: '#b24599',
  [ProVesselTypes.PASSENGER_CARGO]: '#15b7cb',
  [ProVesselTypes.REEFER]: '#615faa',
  [ProVesselTypes.RORO]: '#036483',
  [ProVesselTypes.SPECIALIST]: '#7dc681',
  [ProVesselTypes.UNKNOWN]: '#ce6eac',
};

export function getProVesselTypeColor(type: ProVesselTypes): string {
  return ProVesselTypeIconColors[type] || '#ce6eac';
}

export const ProVesselTypeBackgroundColors: Record<ProVesselTypes, string> = {
  [ProVesselTypes.NO_VESSEL_TYPE]: '#f5e2ee',
  [ProVesselTypes.BULK]: '#fdecd2',
  [ProVesselTypes.CHEMICAL_PRODUCT_TANKER]: '#f6d4d4',
  [ProVesselTypes.CONTAINER]: '#d4d7e4',
  [ProVesselTypes.GAS_CARRIER]: '#d4d7e4',
  [ProVesselTypes.GENERAL_CARGO]: '#dcf1e5',
  [ProVesselTypes.OIL_TANKER]: '#cdd1d5',
  [ProVesselTypes.PASSENGER]: '#f0daeb',
  [ProVesselTypes.PASSENGER_CARGO]: '#cff1f5',
  [ProVesselTypes.REEFER]: '#dfdfee',
  [ProVesselTypes.RORO]: '#cde0e6',
  [ProVesselTypes.SPECIALIST]: '#e5f4e6',
  [ProVesselTypes.UNKNOWN]: '#f5e2ee',
};

export const getProVesselTypeBackgroundColor = (type: ProVesselTypes): string =>
  ProVesselTypeBackgroundColors[type] || '#f5e2ee';

export const MyFleetVesselSVGs: Record<ProVesselTypes, string> = {
  [ProVesselTypes.BULK]: bulk,
  [ProVesselTypes.CHEMICAL_PRODUCT_TANKER]: chemical_tanker,
  [ProVesselTypes.CONTAINER]: container,
  [ProVesselTypes.GAS_CARRIER]: gas_carrier,
  [ProVesselTypes.GENERAL_CARGO]: genral_cargo,
  [ProVesselTypes.OIL_TANKER]: oil_tanker,
  [ProVesselTypes.PASSENGER]: passenger,
  [ProVesselTypes.PASSENGER_CARGO]: passenger_cargo,
  [ProVesselTypes.REEFER]: reefer,
  [ProVesselTypes.RORO]: roro,
  [ProVesselTypes.SPECIALIST]: specialist,
  [ProVesselTypes.UNKNOWN]: unknown,
  [ProVesselTypes.NO_VESSEL_TYPE]: unknown, // Temporary mapping for NO_VESSEL_TYPE as per line 50
};

export function getProVesselTypeSVG(type: ProVesselTypes): string {
  return MyFleetVesselSVGs[type] || MyFleetVesselSVGs[ProVesselTypes.UNKNOWN];
}

export const ProVesselTypeLabels: Record<ProVesselTypes, string> = {
  [ProVesselTypes.NO_VESSEL_TYPE]: 'All Vessels', // Temporary label for NO_VESSEL_TYPE as per line 50
  [ProVesselTypes.BULK]: 'Bulk',
  [ProVesselTypes.CHEMICAL_PRODUCT_TANKER]: 'Chemical/Product Tanker',
  [ProVesselTypes.CONTAINER]: 'Container',
  [ProVesselTypes.GAS_CARRIER]: 'Gas Carrier',
  [ProVesselTypes.GENERAL_CARGO]: 'General Cargo',
  [ProVesselTypes.OIL_TANKER]: 'Oil Tanker',
  [ProVesselTypes.PASSENGER]: 'Passenger',
  [ProVesselTypes.PASSENGER_CARGO]: 'Passenger/Cargo',
  [ProVesselTypes.REEFER]: 'Reefer',
  [ProVesselTypes.RORO]: 'Ro-Ro',
  [ProVesselTypes.SPECIALIST]: 'Specialist',
  [ProVesselTypes.UNKNOWN]: 'Unknown',
};

export function getProVesselTypeLabel(type: ProVesselTypes): string {
  return ProVesselTypeLabels[type] || type;
}

export interface ClubContact {
  name: string | null;
  email: string | null;
  telephone: string | null;
}

export interface CorrespondentContact {
  company: string | null;
  name: string | null;
  email: string | null;
  telephone: string | null;
}

export interface Destination {
  raw: string | null;
  matched_port: {
    name: string;
    unlocode: string;
  } | null;
}

export interface ProReportVesselDestinationResponse {
  imo: string;
  last_updated: string;
  mmsi?: string;
  destination: Destination | null;
}

export interface ProReportCorrespondentAdvice {
  correspondent: string;
  advice: string;
}

export interface VesselSpecificClaimsAdvice {
  summary: string;
  advice: string | null;
}
export interface ClaimsAdvice extends VesselSpecificClaimsAdvice {
  applicable_vessel_types: ProVesselTypes[];
}

export enum SecurityThreatLevel {
  LOW = 'LOW',
  MODERATE = 'MODERATE',
  ELEVATED = 'ELEVATED',
  HIGH = 'HIGH',
  SEVERE = 'SEVERE',
}

export enum SecurityThreatTrend {
  DOWN = 'DOWN',
  UNCHANGED = 'UNCHANGED',
  UP = 'UP',
}

export const translateThreatLevelToText = (
  threatLevel: SecurityThreatLevel
) => {
  switch (threatLevel) {
    case SecurityThreatLevel.LOW:
      return 'Low';
    case SecurityThreatLevel.MODERATE:
      return 'Moderate';
    case SecurityThreatLevel.ELEVATED:
      return 'Elevated';
    case SecurityThreatLevel.HIGH:
      return 'High';
    case SecurityThreatLevel.SEVERE:
      return 'Severe';
    default:
      return 'Unknown';
  }
};

export const translateThreatLevelToColour = (
  threatLevel: SecurityThreatLevel
) => {
  switch (threatLevel) {
    case SecurityThreatLevel.LOW:
    case SecurityThreatLevel.MODERATE:
      return '#4BBF7C';
    case SecurityThreatLevel.ELEVATED:
      return '#F5A100';
    case SecurityThreatLevel.HIGH:
    case SecurityThreatLevel.SEVERE:
      return '#D12721';
    default:
      return 'gray';
  }
};

export const translateThreatTrendToText = (
  threatTrend: SecurityThreatTrend
) => {
  switch (threatTrend) {
    case SecurityThreatTrend.UNCHANGED:
      return 'Unchanged';
    case SecurityThreatTrend.UP:
      return 'Up';
    case SecurityThreatTrend.DOWN:
      return 'Down';
    default:
      return 'Unknown';
  }
};

export const translateThreatTrendToColor = (
  threatTrend: SecurityThreatTrend
) => {
  switch (threatTrend) {
    case SecurityThreatTrend.UNCHANGED:
      return '#03152d';
    case SecurityThreatTrend.UP:
      return '#D12721';
    case SecurityThreatTrend.DOWN:
      return '#4BBF7C';
    default:
      return '#03152d';
  }
};

export interface SecurityThreat {
  type: string;
  level: SecurityThreatLevel;
  trend: SecurityThreatTrend;
}

export interface SecurityAdvice {
  source: string;
  summary: string;
  threats: SecurityThreat[];
}

export interface FirstCallInfo {
  company: string | null;
  email: string | null;
  telephone: string | null;
}

export interface ProReportNonVesselTypeSpecific {
  risk_level: Record<ProVesselTypes, RiskLevel>;
  club_contact?: ClubContact | null;
  correspondent_contact?: CorrespondentContact | null;
  correspondent_advice?: string | null;
  claims_advice?: ClaimsAdvice[] | null;
  first_call_info?: FirstCallInfo | null;
  security_advice?: SecurityAdvice | null;
  port: string;
}

export interface ProReportVesselSpecific {
  vessel_type: ProVesselTypes;
  port: string;
  risk_level: RiskLevel;
  club_contact?: ClubContact | null;
  correspondent_contact?: CorrespondentContact | null;
  correspondent_advice?: string | ProReportCorrespondentAdvice[] | null;
  claims_advice?: VesselSpecificClaimsAdvice[] | null;
  first_call_info?: FirstCallInfo | null;
  security_advice?: SecurityAdvice | null;
}

export type ProReportResponse =
  | ProReportNonVesselTypeSpecific
  | ProReportVesselSpecific;

export function isNonVesselTypeSpecificProReport(
  report: ProReportResponse
): report is ProReportNonVesselTypeSpecific {
  return (
    typeof report.risk_level === 'object' &&
    report.risk_level !== null &&
    !('vessel_type' in report)
  );
}

export function isNonVesselSpecificClaimsAdvice(
  advice: VesselSpecificClaimsAdvice[] | ClaimsAdvice[]
): advice is ClaimsAdvice[] {
  return (
    Array.isArray(advice) &&
    advice.every((item) => 'applicable_vessel_types' in item)
  );
}

export function isRiskLevelRecord(
  riskLevel: RiskLevel | Record<ProVesselTypes, RiskLevel>
): riskLevel is Record<ProVesselTypes, RiskLevel> {
  return typeof riskLevel === 'object' && riskLevel !== null;
}

export interface ProAlert {
  alert_id: string;
  vessel_id: string;
  emails: string[];
  muted: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetProAlertsResponse {
  alerts: ProAlert[];
}

export type ProAlertCreateRequestItem = {
  vessel_id: string;
  emails: string[];
};

export interface ProAlertCreateRequest {
  alerts: ProAlertCreateRequestItem[];
}

export type ProAlertCreateResponseDetails = {
  alert_id: string;
  vessel_id: string;
};

export interface ProAlertCreateResponse {
  status_code?: number;
  alerts?: ProAlertCreateResponseDetails[];
  message: string; // "1 alerts created/updated successfully."
}

export interface ProAlertPostResponse {
  alert: ProAlert;
  success: boolean;
  message?: string;
}

export interface ProAlertDeleteRequest {
  alert_id: string;
}

export interface ProAlertDeleteResponse {
  alert_id: string;
  message: string; // "Alert deleted successfully."
}

export interface ProAlertPatchRequest {
  alert_id: string;
  emails?: string[];
  muted?: boolean;
}

export interface ProAlertPatchEmailsResponse {
  alert_id: string;
  message: string; // "Emails updated."
}

export interface ProAlertPatchMutedResponse {
  alert_id: string;
  muted: boolean;
  message: string; // "Muted status updated."
}

export interface ProAlertErrorResponse {
  message: string;
  field?: string;
  fields?: string[];
  invalid_emails?: string[];
}

export enum ProAlertDialogStatus {
  Idle = 'idle',
  Loading = 'loading',
  Error = 'error',
  Success = 'success',
}
